import {
  getExpDetailsLocalTemplates,
  getLastUpdatedExpTemplate,
  removeExpDetailsLocalTemplate
} from './localExpDetailsViewActions';
import {
  ExperimentTemplateData,
  ExperimentTemplatePreview
} from '@experiment-details/types';
import { normalizeTemplate } from '@experiment-details/utils/templateMigrations';
import experimentApi from '@experiment-management-shared/api/experimentApi';
import { AUTOGENERATED_METRICS_PANEL_NUMBER_LIMIT } from '@experiment-management-shared/constants';
import { generatePersonalTemplate } from '@experiment-management-shared/constants/emptyPersonalTemplate';
import axiosInstance from '@shared/api';
import { Paths } from '@shared/types';
import { cloneDeep, get, set } from 'lodash';

export type LoadExpDetailsTemplateArgs = {
  templateId: string;
  projectId: string;
};
export type LoadPersonalTemplateArgs = {
  templateId?: string;
  projectId: string;
  experimentKey: string;
  sharedConfigTemplateId?: string;
};
export type RemoveExpDetailsTemplateArgs = {
  templateId: string;
  projectId: string;
};
export type UpdateExpDetailsTemplateArgs = {
  template: ExperimentTemplateData;
  projectId: string;
};

const mergePersonalTemplates = (
  target: ExperimentTemplateData,
  source: Partial<ExperimentTemplateData>,
  paths: Paths<ExperimentTemplateData>[]
): ExperimentTemplateData => {
  const result = cloneDeep(target);

  paths.forEach(path => {
    const sourceValue = get(source, path);
    if (sourceValue !== undefined) {
      set(result, path, sourceValue);
    }
  });

  return result;
};

export const sharedPersonalConfigPaths: Paths<ExperimentTemplateData>[] = [
  'chartTemplate.audio',
  'chartTemplate.confusionMatrix',
  'chartTemplate.histogram',
  'chartTemplate.hyperParams',
  'chartTemplate.images',
  'chartTemplate.metricColorsV2',
  'chartTemplate.metrics',
  'chartTemplate.otherParams',
  'chartTemplate.outliers',
  'chartTemplate.text',
  'chartTemplate.transformY',
  'chartTemplate.x_axis',
  'chartTemplate.panels.compareXAxis'
];

export const loadExpDetailsTemplate = async ({
  projectId,
  templateId
}: LoadExpDetailsTemplateArgs) => {
  const remoteTemplateRes = await axiosInstance.get<ExperimentTemplateData>(
    `v2/chart-templates/${templateId}`
  );
  const remoteTemplate = remoteTemplateRes.data;

  const localTemplates = await getExpDetailsLocalTemplates(projectId);

  const localTemplate = localTemplates.find(t => t.templateId === templateId);

  if (!remoteTemplate) {
    throw Error(`Can not find template with id: ${templateId}`);
  }

  if (!localTemplate)
    return {
      ...remoteTemplate,
      chartTemplate: normalizeTemplate({
        ...remoteTemplate.chartTemplate,
        // BE now returns it in a different way (v1 -> v2 endpoints compatibility)
        // and `template_id` will be `null`
        template_id: remoteTemplate.templateId,
        charts: []
      })
    };

  const personalTemplateData = getLastUpdatedExpTemplate(
    remoteTemplate,
    localTemplate
  );

  return {
    ...personalTemplateData,
    chartTemplate: normalizeTemplate({
      ...personalTemplateData.chartTemplate,
      charts: []
    })
  };
};

export const loadExpDetailsPersonalTemplate = async ({
  templateId,
  projectId,
  experimentKey,
  sharedConfigTemplateId
}: LoadPersonalTemplateArgs) => {
  let remoteTemplate: ExperimentTemplateData;
  if (templateId) {
    const remoteTemplateRes = await axiosInstance.get<ExperimentTemplateData>(
      `v2/chart-templates/${templateId}`
    );
    remoteTemplate = remoteTemplateRes.data;
  } else {
    remoteTemplate = generatePersonalTemplate({
      experimentKey,
      projectId
    });
  }

  if (sharedConfigTemplateId) {
    const sharedTemplateRes = await axiosInstance.get<ExperimentTemplateData>(
      `v2/chart-templates/${sharedConfigTemplateId}`
    );

    remoteTemplate = mergePersonalTemplates(
      remoteTemplate,
      sharedTemplateRes.data,
      sharedPersonalConfigPaths
    );
  }

  const localTemplates = await getExpDetailsLocalTemplates(projectId);
  const localTemplate = localTemplates.find(t => t.personalView);

  if (!localTemplate)
    return {
      ...remoteTemplate,
      chartTemplate: normalizeTemplate({
        ...remoteTemplate.chartTemplate,
        charts: []
      })
    };

  const personalTemplateData = getLastUpdatedExpTemplate(
    remoteTemplate,
    localTemplate
  );

  return {
    ...personalTemplateData,
    chartTemplate: normalizeTemplate({
      ...personalTemplateData.chartTemplate,
      charts: []
    })
  };
};

export const getMetricNamesForExperimentKeys = async (
  experimentKeys: string[],
  signal?: AbortSignal
) => {
  const response = await experimentApi.getMetricNamesForExperiments(
    experimentKeys,
    signal
  );

  return response?.data?.metrics?.slice(
    0,
    AUTOGENERATED_METRICS_PANEL_NUMBER_LIMIT
  );
};

export type LoadExpTemplateListArgs = {
  experimentKey: string;
  projectId: string;
};
export const loadExpTemplateList = async ({
  projectId,
  experimentKey
}: LoadExpTemplateListArgs) => {
  const res = await axiosInstance.get<{
    templates: ExperimentTemplatePreview[];
  }>(
    `v2/chart-templates?projectId=${projectId}&experimentKey=${experimentKey}`
  );

  return res.data.templates || [];
};

export const removeExpDetailsTemplate = async ({
  projectId,
  templateId
}: RemoveExpDetailsTemplateArgs) => {
  const res = await axiosInstance.delete(
    `v2/chart-templates/${templateId}?projectId=${projectId}`
  );
  removeExpDetailsLocalTemplate(projectId, templateId);
  return res;
};

export const saveExpDetailsTemplate = async ({
  chartTemplate,
  ...rest
}: ExperimentTemplateData) => {
  return await axiosInstance.put<ExperimentTemplateData>(
    `v2/chart-templates/${rest.templateId}`,
    {
      template: chartTemplate,
      projectId: rest.projectId
    }
  );
};

export const newExpDetailsTemplate = async (
  { chartTemplate, ...rest }: ExperimentTemplateData,
  experimentKey: string
) => {
  return await axiosInstance.post<ExperimentTemplateData>(
    `v2/chart-templates`,
    {
      template: {
        ...chartTemplate,
        template_id: null
      },
      projectId: rest.projectId,
      experimentKey
    }
  );
};
